import alt from 'components/Dispatcher';
import immutableStore from 'alt-utils/lib/ImmutableUtil';
import {Map, Record} from 'immutable';
import callDetailsFromDisplayAndSearchFields from 'utils/callDetailsFromDisplayAndSearchFields';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import CallDeleteActions from 'actions/CallDeleteActions';
import CallSyncCRMStore from 'stores/crm/CallSyncCRMStore';
import LibraryStore from 'stores/library/LibraryStore';
import SearchFieldsStore from 'stores/search/SearchFieldsStore';
import SearchStore from 'stores/SearchStore';
import PlaybackActions from 'actions/PlaybackActions';
import FileUploadStore from 'stores/files/FileUploadStore';

/**
 * `FileUploadCallDetailsStore` stores the details of different calls whose order is ranked by
 * each `field_schema` object within the list of `search_results.fields`
 */
@immutableStore
class FileUploadCallDetailsStore {
  constructor() {
    this.state = Record({
      // `callDetails` maps `FileUpload.id` values to their corresponding `details` which is
      // a immutable.OrderedMap whose keys are the names of the search fields and values
      // the values of these fields
      callDetails: Map(),
      // `showCallDetailsForFileUpload` points to the `FileUpload.id` value of a call whose details
      // modal dialog should be displayed
      showCallDetailsForFileUpload: null,
      // `showCallSyncCRMForFileUpload` points to the `FileUpload.id` value of a call whose sync-crm dialog should be
      // displayed in order to allow the caller / manager of that call to sync its CRM metadata
      showCallSyncCRMForFileUpload: null,
      // `showCallSyncCRMRecordDialog` points to the `FileUpload.id` value of a call whose crm-record dialog should be
      // displayed in order to allow the user to open its CRM link page (a page for contact / account / lead)
      showCallSyncCRMRecordDialog: null,
      showAddToLibraryDialog: null,
      transcriptionDialogFileId: null,
      callDeleteDialogFileId: null,
      flashCallFeedbackActions: false,
      blockCallPlaying: false,
    })();
    this.bindActions(FileUploadCallDetailsActions);
    this.bindActions(CrmSyncActions);
    this.bindActions(CallDeleteActions);
  }

  /**
   * Update this.state.get('showCallDetails') to null so that the <CallCardDetailsDialog /> will disappear
   * @param  {string} id A FileUpload.id value
   */
  onHideCallDetails(id) {
    const state = this.state.set('showCallDetailsForFileUpload', null);
    this.setState(state);
    PlaybackActions.dialogMaskHidden.defer();
  }

  onShowAddToLibraryDialog(id) {
    this.setState(this.state.set('showAddToLibraryDialog', id));
    PlaybackActions.dialogMaskShown.defer();
  }

  onHideAddToLibraryDialog() {
    this.setState(this.state.set('showAddToLibraryDialog', null));
    PlaybackActions.dialogMaskHidden.defer();
  }

  showTranscriptionDialog(id) {
    this.setState(this.state.set('transcriptionDialogFileId', id));
    PlaybackActions.dialogMaskShown.defer();
  }

  hideTranscriptionDialog() {
    this.setState(this.state.set('transcriptionDialogFileId', null));
    PlaybackActions.dialogMaskHidden.defer();
  }

  blockCallPlaying() {
    this.setState(this.state.set('blockCallPlaying', true));
  }

  unblockCallPlaying() {
    this.setState(this.state.set('blockCallPlaying', false));
  }

  showCallDeleteDialog(id) {
    this.setState(this.state.set('callDeleteDialogFileId', id));
    PlaybackActions.dialogMaskShown.defer();
  }

  hideCallDeleteDialog() {
    this.setState(this.state.set('callDeleteDialogFileId', null));
    PlaybackActions.dialogMaskHidden.defer();
  }

  deleteCallSuccess() {
    this.hideCallDeleteDialog();
  }

  /**
   * Callback for `FileUploadCallDetailsActions.showCallDetails`. In this handler, we check if
   * this.state.get('callDetails') contains the correct ordered details for a FileUpload with `id` or not.
   * If it does not, then we use `SearchFieldsStore` and `SearchStore` to populate `this.state.get('callDetails')`
   * with its details
   * @param  {string} id A `FileUpload.id` value
   */
  onShowCallDetails(id) {
    PlaybackActions.dialogMaskShown.defer();
    let state = this.state;
    if (!this.state.getIn(['callDetails', id])) {
      // Populate this.state.get('callDetails') with the details for this call
      const searchResults = SearchStore.getState().get('searchResults');
      const results = searchResults.results;
      let fileUpload = results
        .filter(result => {
          return result.id === id;
        })
        .first();
      if (!fileUpload) {
        fileUpload = LibraryStore.getState()
          .get('searchResults')
          .results.filter(result => {
            return result.id === id;
          })
          .first();
      }
      if (!fileUpload) {
        fileUpload = FileUploadStore.getState().fileUploadsMap.get(id);
      }
      if (fileUpload) {
        const displayFields = fileUpload.fields;
        let searchFields = SearchFieldsStore.getState().get('fullSearchFields');
        const callDetails = callDetailsFromDisplayAndSearchFields(displayFields, searchFields);
        state = state.setIn(['callDetails', id], callDetails);
      }
    }
    state = state.set('showCallDetailsForFileUpload', id);
    this.setState(state);
  }

  /**
   * Callback for `FileUploadCallDetailsActions.showCallSyncCRM`. In this handler, we set
   * this.state.get('showCallSyncCRMForFileUpload') to `id` so that the front-end can show a two-way CRM sync dialog for
   * the given FileUpload object
   * @param id {string} FileUpload.id value
   */
  onShowCallSyncCRM(id) {
    PlaybackActions.dialogMaskShown.defer();
    const state = this.state.set('showCallSyncCRMForFileUpload', id);
    this.setState(state);
  }

  /**
   * Callback for `FileUploadCallDetailsActions.hideCallSyncCRM`. In this handler, we set
   * this.state.get('showCallSyncCRMForFileUpload') to null so that the front-end can hide the open sync-crm dialog
   */
  onHideCallSyncCRM() {
    PlaybackActions.dialogMaskHidden.defer();
    const state = this.state.set('showCallSyncCRMForFileUpload', null);
    this.setState(state);
    setImmediate(() => {
      CrmSyncActions.clearCurrentPrefixSearchContactsAndLeadsValues();
      CrmSyncActions.clearAccountsAndOpportunitiesValues();
      CrmSyncActions.clearContactsByAccountId();
      CrmSyncActions.clearAccountsAndOpportunitiesOptions();
      CrmSyncActions.clearContactsAndLeadsOptions();
      if (CallSyncCRMStore.getState().get('fileUploadId') !== null) {
        CrmSyncActions.clearSyncCRMFileUploadId();
      }
    });
  }

  /**
   * Callback for `FileUploadCallDetailsActions.showCRMRecordDialog`. In this handler, we set
   * this.state.get('showCallSyncCRMRecordDialog') to `id` so that the front-end can show a CRM record dialog for the
   * given FileUpload object
   * @param id {string} FileUpload.id value
   */
  onShowCRMRecordDialog(id) {
    PlaybackActions.dialogMaskShown.defer();
    const state = this.state.set('showCallSyncCRMRecordDialog', id);
    this.setState(state);
  }

  /**
   * Callback for `FileUploadCallDetailsActions.hideCRMRecordDialog`. In this handler, we set
   * this.state.get('hideCRMRecordDialog') to null so that the front-end can hide the CRM record dialog
   */
  onHideCRMRecordDialog() {
    PlaybackActions.dialogMaskHidden.defer();
    const state = this.state.set('showCallSyncCRMRecordDialog', null);
    this.setState(state);
  }

  onFlashCallFeedbackActions(on) {
    this.setState(this.state.set('flashCallFeedbackActions', on));
  }
}

export default alt.createStore(FileUploadCallDetailsStore, 'FileUploadCallDetailsStore');
